import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
    const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
    // Co má systém zrovna nastavené — kvůli volbě „Systém". Drží se ve stavu,
    // ne jen ve `matchMedia` při renderu: jinak by se přepnutí motivu v telefonu
    // projevilo až po překreslení z jiného důvodu.
    const [systemTmavy, setSystemTmavy] = useState(
        () => typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches,
    );

    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e) => setSystemTmavy(e.matches);
        mq.addEventListener('change', handler);
        setSystemTmavy(mq.matches);
        return () => mq.removeEventListener('change', handler);
    }, []);

    // Motiv, který je opravdu vidět — `theme` může být „system".
    //
    // Kontext dřív vydával jen `theme`, takže si každá stránka počítala `isDark`
    // sama (a pokaždé stejným řádkem). KPI Dashboard a Energie sáhly po
    // `resolvedTheme`, který tu nebyl: `isDark` jim vycházel vždycky `false`
    // a v tmavém motivu kreslily světlé sklo.
    const jeTmavy = theme === 'dark' || (theme === 'system' && systemTmavy);
    const resolvedTheme = jeTmavy ? 'dark' : 'light';

    useEffect(() => {
        const apply = (dark) => {
            const root = document.documentElement;
            root.classList.toggle('dark', dark);
            // Šablona (welcome.blade.php) dává <html> inline pozadí podle tématu
            // při načtení — kvůli prvnímu vykreslení ještě před CSS. Po přepnutí
            // tématu za běhu by tam zůstalo a přebilo CSS: světlá aplikace na
            // černém pozadí. CSS je v tu chvíli načtené, takže inline hodnotu
            // uklidíme a pozadí řídí `html { background: var(--surface-base) }`.
            root.style.background = '';
            // …a barvu nastavíme zpátky natvrdo. Plocha za výřezem (safe-area,
            // okolí ostrůvku) se maluje z pozadí <html>, ale iOS ji po pouhé
            // změně CSS proměnné nepřekreslí — drží starou až do restartu
            // aplikace. Změna inline stylu překreslení vynutí. Jen
            // `background-color`, ne zkratka: ta by smazala i brandovou záři
            // (`--zare-mobil` je background-image).
            root.style.backgroundColor = dark ? '#0A0A0C' : '#F5F5F7';
            // Na <body> pozadí NEPATŘÍ — je to blokový potomek a jeho pozadí se
            // kreslí až ZA prvky se záporným z-indexem, takže překryje brandovou
            // záři (.os-bg). Stojí to v app.css a stejně jsem do toho spadl:
            // zkusil jsem tudy vynutit překreslení safe-area a zabil tím záři.
            // Pás za výřezem leží MIMO webovou plochu (status bar style je
            // `default`, viz welcome.blade.php) a maluje ho iOS, ne CSS. Na
            // změnu atributu `content` nereaguje — zkoušíme proto celý prvek
            // vyměnit, což WebKit bere jako nový údaj. Když ani to nepomůže,
            // je to mez platformy: barvu si iOS bere při spuštění a bez
            // `black-translucent` (který má na iOS 26/27 horší vady) ji za
            // běhu změnit nejde.
            const barva = dark ? '#0A0A0C' : '#F5F5F7';
            document.querySelector('meta[name="theme-color"]')?.remove();
            const meta = document.createElement('meta');
            meta.name = 'theme-color';
            meta.content = barva;
            document.head.appendChild(meta);
            // Přidat theme-ready po první aplikaci – spustí smooth transitions
            setTimeout(() => document.body.classList.add('theme-ready'), 50);
        };

        apply(jeTmavy);
        localStorage.setItem('theme', theme);
    }, [theme, jeTmavy]);

    return (
        <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme, isDark: jeTmavy }}>
            {children}
        </ThemeContext.Provider>
    );
}

export const useTheme = () => useContext(ThemeContext);
